import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { updateUser, changePassword } from '../../services/userService';
import { getUserPayments, getPaymentDetails, PaymentWithItems, Payment } from '../../services/paymentService';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Form components
import FormProvider from '../../components/forms/FormProvider';
import FormTextField from '../../components/forms/FormTextField';
import FormPasswordField from '../../components/forms/FormPasswordField';
import FormSwitch from '../../components/forms/FormSwitch';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

// Define form data types
interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  profile: {
    school: string;
    grade: string;
    parentEmail: string;
    phone: string;
    showAchievements: boolean;
    profileColor: string;
  }
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Validation schemas
const profileSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  username: yup.string(),
  profile: yup.object().shape({
    school: yup.string(),
    grade: yup.string()
      .test('is-valid-grade', 'Grade must be between 5 and 8', (value) => {
        if (!value) return true;
        const num = parseInt(value);
        return !isNaN(num) && num >= 5 && num <= 8;
      }),
    parentEmail: yup.string().email('Please enter a valid email'),
    phone: yup.string(),
    showAchievements: yup.boolean(),
    profileColor: yup.string()
  })
});

const passwordSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup.string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
});

function ProfilePage() {
  console.log('ProfilePage component is rendering');
  const { authState, updateCurrentUser } = useAuth();
  const { user } = authState;
  
  // Log user info for debugging
  console.log('User in ProfilePage:', user);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Profile edit state
  const [isEditing, setIsEditing] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Password state
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Payments state
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentWithItems | null>(null);
  const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);

  // Initialize profile form with explicit typing
  const profileMethods = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema) as unknown as Resolver<ProfileFormData>,
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      username: user?.profile?.username || '',
      profile: {
        school: user?.profile?.school || '',
        grade: user?.profile?.grade?.toString() || '',
        parentEmail: user?.profile?.parentEmail || '',
        phone: user?.profile?.phone || '',
        showAchievements: user?.profile?.showAchievements === undefined ? true : user?.profile?.showAchievements,
        profileColor: user?.profile?.profileColor || '#1976d2' // Default to primary blue
      }
    }
  });

  // Initialize password form with explicit typing
  const passwordMethods = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  // Fetch user payments
  useEffect(() => {
    // Only load payments when the payments tab is selected
    if (tabValue === 3 && user) {
      loadUserPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, user]);

  // Load user payments
  const loadUserPayments = async () => {
    if (!user) return;
    
    setPaymentsLoading(true);
    setPaymentsError(null);
    
    try {
      const userPayments = await getUserPayments();
      setPayments(userPayments);
    } catch (error) {
      console.error('Error loading user payments:', error);
      setPaymentsError('Failed to load payment history. Please try again later.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Load payment details
  const loadPaymentDetails = async (paymentId: number) => {
    setPaymentDetailsLoading(true);
    
    try {
      const paymentDetails = await getPaymentDetails(paymentId);
      setSelectedPayment(paymentDetails);
    } catch (error) {
      console.error('Error loading payment details:', error);
      // Keep the current selection if there's an error
    } finally {
      setPaymentDetailsLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing, reset form to original values
      profileMethods.reset({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        username: user?.profile?.username || '',
        profile: {
          school: user?.profile?.school || '',
          grade: user?.profile?.grade?.toString() || '',
          parentEmail: user?.profile?.parentEmail || '',
          phone: user?.profile?.phone || '',
          showAchievements: user?.profile?.showAchievements === undefined ? true : user?.profile?.showAchievements,
          profileColor: user?.profile?.profileColor || '#1976d2' // Default to primary blue
        }
      });
    }
    setIsEditing(!isEditing);
    setProfileError(null);
    setProfileSuccess(null);
  };

  // Handle profile save
  const handleSaveProfile = async (data: ProfileFormData) => {
    if (!user) return;
    
    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(null);
    
    try {
      // Convert grade to number if present and move username into profile
      const updatedData = {
        ...data,
        profile: {
          ...data.profile,
          username: data.username, // Move username from top level to profile
          grade: data.profile.grade ? parseInt(data.profile.grade) : undefined
        }
      };
      
      const updatedUser = await updateUser(user.id, updatedData);
      
      // Update the user in the auth context to reflect changes immediately
      updateCurrentUser(updatedUser);
      
      setProfileSuccess('Profile updated successfully.');
      setIsEditing(false);
    } catch (error) {
      setProfileError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle password change
  const handleChangePassword = async (data: PasswordFormData) => {
    if (!user) return;
    
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);
    
    try {
      await changePassword(
        user.id,
        data.currentPassword,
        data.newPassword
      );
      
      setPasswordSuccess('Password changed successfully.');
      passwordMethods.reset();
    } catch (error) {
      setPasswordError('Failed to change password. Please check your current password and try again.');
      console.error('Error changing password:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <MainLayout>
        <Typography variant="h5" component="h1" sx={{ textAlign: 'center', mt: 4 }}>
          User information not available
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Typography variant="h4" component="h1" gutterBottom>
        My Profile
      </Typography>
      
      {/* Profile Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          py: 4, 
          px: 3, 
          mb: 4, 
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          gap: 3
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              bgcolor: isEditing 
                ? (profileMethods.watch('profile.profileColor') || '#1976d2') 
                : (user.profile?.profileColor || ((user.role === 'admin' || user.role === 'super_admin') ? 'primary.main' : 'secondary.main')),
              fontSize: 40,
              transition: 'background-color 0.3s ease' // Smooth transition when color changes
            }}
          >
            {user.firstName.charAt(0)}
          </Avatar>
          {isEditing && (
            <Tooltip title="Change profile color">
              <Box 
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 1,
                  border: '2px solid white',
                  zIndex: 1,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  // Create a hidden input element to handle the color picker
                  const input = document.createElement('input');
                  input.type = 'color';
                  input.value = profileMethods.watch('profile.profileColor') || '#1976d2';
                  
                  // Handle real-time color changes
                  input.addEventListener('input', (e) => {
                    // This updates the color as the user drags the slider
                    profileMethods.setValue('profile.profileColor', (e.target as HTMLInputElement).value, {
                      shouldDirty: true, // Mark form as dirty to enable submit
                      shouldValidate: true
                    });
                  });
                  
                  // Also handle the final change event
                  input.addEventListener('change', (e) => {
                    profileMethods.setValue('profile.profileColor', (e.target as HTMLInputElement).value, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  });
                  
                  input.click();
                }}
              >
                <EditIcon fontSize="small" color="primary" />
              </Box>
            </Tooltip>
          )}
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
            {user.firstName} {user.lastName}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {user.role === 'admin' || user.role === 'super_admin' ? (
              <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
            ) : (
              <SchoolIcon sx={{ color: 'secondary.main', mr: 1 }} />
            )}
            <Typography variant="body1" color="text.secondary">
              {user.role === 'super_admin' ? 'Super Administrator' : 
 user.role === 'admin' ? 'Administrator' : 'Student'}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {user.email}
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            Member since {new Date(user.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>
      
      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="profile tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            label="Account Information" 
            id="profile-tab-0" 
            aria-controls="profile-tabpanel-0" 
          />
          <Tab 
            label="Security" 
            id="profile-tab-1" 
            aria-controls="profile-tabpanel-1" 
          />
          {user.role === 'student' && (
            <Tab 
              label="My Sessions" 
              id="profile-tab-2" 
              aria-controls="profile-tabpanel-2" 
            />
          )}
          <Tab
            label="Payment History"
            id="profile-tab-3"
            aria-controls="profile-tabpanel-3"
            icon={<PaymentIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>
      
      {/* Profile Information Tab */}
      <TabPanel value={tabValue} index={0}>
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Personal Information
              </Typography>
              
              <Button
                startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                onClick={handleEditToggle}
                color={isEditing ? 'error' : 'primary'}
                size="small"
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </Box>
            
            {profileError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {profileError}
              </Alert>
            )}
            
            {profileSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {profileSuccess}
              </Alert>
            )}
            
            <FormProvider methods={profileMethods} onSubmit={handleSaveProfile}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="firstName"
                    label="First Name"
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="lastName"
                    label="Last Name"
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="email"
                    label="Email Address"
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="username"
                    label="Username (for Leaderboard)"
                    disabled={!isEditing}
                    margin="normal"
                    helperText="If left blank, your first name and last initial will be shown"
                  />
                </Grid>
              </Grid>
              
              {user.role === 'student' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Student Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormTextField
                        name="profile.school"
                        label="School"
                        disabled={!isEditing}
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormTextField
                        name="profile.grade"
                        label="Grade"
                        disabled={!isEditing}
                        type="number"
                        inputProps={{ min: 5, max: 8 }}
                        helperText="Grades 5-8"
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormTextField
                        name="profile.parentEmail"
                        label="Parent Email"
                        disabled={!isEditing}
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormTextField
                        name="profile.phone"
                        label="Phone Number"
                        disabled={!isEditing}
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ mt: 2, mb: 1 }} />
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Privacy Settings
                        </Typography>
                        
                        <FormSwitch
                          name="profile.showAchievements"
                          label="Allow other students to view my achievements"
                          description="When enabled, other students can view your achievements on the leaderboard"
                          disabled={!isEditing}
                        />
                        
                        {/* Profile color picker moved to avatar */}
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
              
              {isEditing && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={profileLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={profileLoading}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
            </FormProvider>
          </CardContent>
        </Card>
      </TabPanel>
      
      {/* Security Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Change Password
            </Typography>
            
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {passwordSuccess}
              </Alert>
            )}
            
            <FormProvider methods={passwordMethods} onSubmit={handleChangePassword}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormPasswordField
                    name="currentPassword"
                    label="Current Password"
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormPasswordField
                    name="newPassword"
                    label="New Password"
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormPasswordField
                    name="confirmPassword"
                    label="Confirm New Password"
                    required
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={passwordLoading ? <CircularProgress size={20} /> : <LockIcon />}
                  disabled={passwordLoading}
                >
                  Change Password
                </Button>
              </Box>
            </FormProvider>
            
            <Divider sx={{ my: 4 }} />
            
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Account Security
            </Typography>
            
            <Typography variant="body2" paragraph>
              Last login: April 15, 2025, 2:30 PM
            </Typography>
            
            <Typography variant="body2" paragraph>
              IP Address: 192.168.1.1
            </Typography>
            
            <Typography variant="body2" paragraph>
              Browser: Chrome on MacOS
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                If you notice any suspicious activity on your account, please change your password immediately
                and contact support.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>
      
      {/* Student Sessions Tab */}
      {user.role === 'student' && (
        <TabPanel value={tabValue} index={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                My Sessions
              </Typography>
              
              <Typography variant="body2" paragraph>
                View your enrolled sessions and activity history.
              </Typography>
              
              <Button 
                variant="contained"
                component="a"
                href="/dashboard"
                sx={{ mt: 1 }}
              >
                Go to My Sessions
              </Button>
            </CardContent>
          </Card>
        </TabPanel>
      )}

      {/* Payment History Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Payment History
              </Typography>
              
              {selectedPayment && (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setSelectedPayment(null)}
                >
                  Back to All Payments
                </Button>
              )}
            </Box>
            
            {paymentsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentsError}
              </Alert>
            )}
            
            {paymentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : payments.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                You don't have any payment records yet.
              </Alert>
            ) : !selectedPayment ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.transactionDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={payment.paymentStatus}
                            color={
                              payment.paymentStatus === 'captured' || payment.paymentStatus === 'approved' 
                                ? 'success' 
                                : payment.paymentStatus === 'pending' 
                                  ? 'warning' 
                                  : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          ${payment.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {payment.paymentMethod === 'paypal' ? 'PayPal' : payment.paymentMethod}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => loadPaymentDetails(payment.id)}
                            disabled={paymentDetailsLoading}
                            startIcon={paymentDetailsLoading ? <CircularProgress size={16} /> : <ReceiptIcon />}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Payment #{selectedPayment.id}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Transaction Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedPayment.transactionDate).toLocaleString()}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Status
                      </Typography>
                      <Chip 
                        label={selectedPayment.paymentStatus}
                        color={
                          selectedPayment.paymentStatus === 'captured' || selectedPayment.paymentStatus === 'approved' 
                            ? 'success' 
                            : selectedPayment.paymentStatus === 'pending' 
                              ? 'warning' 
                              : 'error'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography variant="body1">
                        {selectedPayment.paymentMethod === 'paypal' 
                          ? 'PayPal' 
                          : selectedPayment.paymentMethod}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        ${selectedPayment.amount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                  Items
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPayment.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            Session #{item.sessionId}
                          </TableCell>
                          <TableCell align="right">
                            ${Number(item.amount + item.discountApplied).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {item.discountApplied > 0 ? `-$${Number(item.discountApplied).toFixed(2)}` : '$0.00'}
                          </TableCell>
                          <TableCell align="right">
                            ${Number(item.amount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {selectedPayment.paypalOrderId && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      PayPal Order ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {selectedPayment.paypalOrderId}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </MainLayout>
  );
}

export default ProfilePage;